import { useCallback, useContext, useEffect, useState } from 'react';
import { QuizContext } from 'src/views/Quiz/Quiz.context';
import { validateEmail } from 'src/helpers/utils';
import { QUIZ_EVENT_TYPES } from 'src/types/Quiz';
import { trackEmailSubmitted } from 'src/analytics/Events';
import { Vertical } from 'src/constants/Verticals';
import { Input } from '@get-mosh/msh-shared';
import GoogleLoginButton from 'src/components/GoogleLoginButton/GoogleLoginButton';
import AppleLoginButton from 'src/components/AppleLoginButton';
import { useUser } from 'src/hooks/User/useUser';
import { addTraits, identify } from 'src/analytics/Analytics';
import { env } from 'src/env.mjs';

const QuizLeadForm = () => {
    const {
        quizToken,
        quizType,
        quizName,
        quizVersion,
        currentQuestion,
        handleSubmitAnswer,
        state,
        handleNavigateToQuestion,
    } = useContext(QuizContext);
    const {
        questionId,
        questionText,
        questionExplanation,
        userAnswers,
        answerList,
    } = currentQuestion || {};
    const { data: user } = useUser();

    const [pageTransitioning] = state.pageTransitioning;
    const [, setContinueVisible] = state.continueVisible;
    const [continueDisabled, setContinueDisabled] = state.continueDisabled;

    const [email, setEmail] = useState('');
    const [emailTouched, setEmailTouched] = useState(false);
    const validEmail = validateEmail(email);
    const [userCreatedMethod, setUserCreatedMethod] = useState('');

    // Set existing answer
    useEffect(() => {
        let newEmail: string | null = null;
        if (userAnswers.length === 3) {
            newEmail = userAnswers[0].answerText;
        }

        if (newEmail) setEmail(newEmail);
    }, [userAnswers]);

    // Handle submitting
    const handleSubmit = useCallback(() => {
        if (validEmail.success) {
            if (!continueDisabled && questionId) {
                handleSubmitAnswer(
                    {
                        questionId: questionId,
                        questionText: questionText,
                        answerIds: [answerList[0].answerId],
                        answerTexts: [email],
                    },
                    (response) => {
                        addTraits({
                            email,
                        });
                        trackEmailSubmitted(Vertical[quizType.toUpperCase()], {
                            quiz_name: `${quizName} - ${quizVersion}`,
                            label: 'Email Submitted',
                            user_created_source: `Web ${env.NEXT_PUBLIC_BRAND} ${quizVersion} ${quizName}`,
                            user_created_method: 'Email',
                        });
                        handleNavigateToQuestion(response.currentQuestionId);
                    },
                );
            }
        }
    }, [
        validEmail.success,
        continueDisabled,
        questionId,
        handleSubmitAnswer,
        questionText,
        answerList,
        email,
        quizType,
        quizName,
        quizVersion,
        handleNavigateToQuestion,
    ]);

    useEffect(() => {
        if (user) {
            identify(user.id, {
                email: user.email,
                phone: user.phoneNumber,
                country_code: env.NEXT_PUBLIC_COUNTRY_CODE,
                brand: env.NEXT_PUBLIC_BRAND,
                uuid: user.uuid,
            });
            handleSubmitAnswer(
                {
                    questionId: questionId,
                    questionText: questionText,
                    answerIds: [answerList[0].answerId],
                    answerTexts: [user.email],
                },
                (response) => {
                    trackEmailSubmitted(Vertical[quizType.toUpperCase()], {
                        quiz_name: `${quizName} - ${quizVersion}`,
                        label: 'Email Submitted',
                        user_created_source: `Web ${env.NEXT_PUBLIC_BRAND} ${quizVersion} ${quizName}`,
                        user_created_method: userCreatedMethod,
                    });
                    handleNavigateToQuestion(response.currentQuestionId);
                },
            );
        }
    }, [
        userCreatedMethod,
        handleNavigateToQuestion,
        handleSubmitAnswer,
        user,
        quizType,
        quizName,
        quizVersion,
        answerList,
        email,
        continueDisabled,
        questionId,
        questionText,
    ]);

    // Continue click listener
    useEffect(() => {
        window.addEventListener(QUIZ_EVENT_TYPES.continueClick, handleSubmit);

        return () =>
            window.removeEventListener(
                QUIZ_EVENT_TYPES.continueClick,
                handleSubmit,
            );
    }, [handleSubmit]);

    // Enter key press listener
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.key === 'Enter') handleSubmit();
        };
        window.addEventListener('keydown', handleKeyPress, false);

        return () =>
            window.removeEventListener('keydown', handleKeyPress, false);
    }, [handleSubmit]);

    // Continue state
    useEffect(() => {
        if (!pageTransitioning) {
            setContinueVisible(true);
            setContinueDisabled(
                email === null ||
                    email.length === 0 ||
                    !validEmail.success ||
                    !validateEmail(email).success,
            );
        }
    }, [
        email,
        emailTouched,
        validEmail,
        pageTransitioning,
        setContinueDisabled,
        setContinueVisible,
    ]);

    const handleButtonClick = (
        e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>,
    ) => {
        const buttonType = (e.target as HTMLElement)
            .closest('[data-button]')
            ?.getAttribute('data-button');
        if (buttonType) {
            setUserCreatedMethod(buttonType);
        }
    };

    return (
        <div className="flex items-center justify-center">
            <div className="flex max-w-[300px] flex-col gap-6 sm:max-w-[400px]">
                <div className="flex flex-col gap-2">
                    <h4>{questionText}</h4>
                    {questionExplanation ? <p>{questionExplanation}</p> : null}
                </div>
                <div
                    className="flex flex-col items-center gap-2"
                    onClick={handleButtonClick}
                >
                    <GoogleLoginButton quizToken={quizToken} navigate={false} />
                    <AppleLoginButton quizToken={quizToken} navigate={false} />
                </div>

                <div className="flex flex-row items-center gap-4">
                    <div className="border-grey-200 flex-1 border-t"></div>
                    <span className="text-grey-400 text-sm">
                        or enter your email
                    </span>
                    <div className="border-grey-200 flex-1 border-t"></div>
                </div>
                <Input
                    type="email"
                    data-testid="email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="john@example.com"
                    error={
                        emailTouched && validEmail.error
                            ? validEmail.error.issues[0]?.message
                            : ''
                    }
                    onFocus={() => emailTouched && setEmailTouched(false)}
                    onBlur={() => !emailTouched && setEmailTouched(true)}
                />
            </div>
        </div>
    );
};

export default QuizLeadForm;
